import Phaser from "phaser";

/**
 * Stamina meter UI component
 * Shows a horizontal yellow progress bar in the upper right corner
 */
export class StaminaMeter {
  private scene: Phaser.Scene;
  private graphics: Phaser.GameObjects.Graphics;
  private container: Phaser.GameObjects.Container;
  private stamina: number = StaminaMeter.STAMINA_MAX; // 0 to maxStamina
  private width: number = 200; // Width of progress bar (150px wide)
  private height: number = 12; // Height of progress bar (same as ammo meter)
  private x: number;
  private y: number;

  // Refill system
  private isRefilling: boolean = false; // Whether stamina is currently below max

  public static STAMINA_DEPLETION_RATE: number = 60 / 3000; // 60 stamina in 2000ms (2 seconds)
  public static STAMINA_REPLENISH_RATE: number = 60 / 6000; // 60 stamina in 6000ms (6 seconds)
  public static STAMINA_MAX: number = 60; // Maximum stamina value

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    uiLayer?: Phaser.GameObjects.Container,
  ) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    // Create graphics for the meter
    this.graphics = scene.add.graphics();
    this.graphics.setScrollFactor(0);
    this.graphics.setDepth(10100);

    // Create container
    this.container = scene.add.container(x, y);
    this.container.setScrollFactor(0);
    this.container.setDepth(10100);
    this.container.setVisible(false); // Hidden by default
    this.container.add(this.graphics);

    // Ensure stamina meter only renders on UI camera
    if ((scene as any).cameras?.main) {
      (scene as any).cameras.main.ignore(this.graphics);
      (scene as any).cameras.main.ignore(this.container);
    }

    // Add to UI layer if available
    if (uiLayer) {
      uiLayer.add(this.container);
    }

    // Draw initial meter
    this.draw();

    // Register update listener
    scene.events.on("update", this.update, this);
  }

  private draw(): void {
    this.graphics.clear();

    // Only draw if stamina is below max
    if (this.stamina >= StaminaMeter.STAMINA_MAX) {
      return;
    }

    // Draw background bar (dark gray)
    this.graphics.fillStyle(0x333333, 0.8);
    this.graphics.fillRect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );

    // Draw yellow progress fill
    const progressWidth =
      (this.stamina / StaminaMeter.STAMINA_MAX) * this.width;
    this.graphics.fillStyle(0xffff00, 1);
    this.graphics.fillRect(
      -this.width / 2,
      -this.height / 2,
      progressWidth,
      this.height,
    );

    // Draw border
    this.graphics.lineStyle(1, 0x000000, 0.8);
    this.graphics.strokeRect(
      -this.width / 2,
      -this.height / 2,
      this.width,
      this.height,
    );
  }

  /**
   * Set stamina value (0 to 100)
   */
  setStamina(value: number): void {
    const newStamina = Phaser.Math.Clamp(value, 0, StaminaMeter.STAMINA_MAX);

    // If stamina is being decreased (used), start refilling
    if (newStamina < this.stamina) {
      this.isRefilling = true;
    }

    // If stamina reaches max, stop refilling
    if (newStamina === StaminaMeter.STAMINA_MAX) {
      this.isRefilling = false;
    }

    this.stamina = newStamina;
    this.draw();
    // Only show meter when stamina is below max
    this.container.setVisible(this.stamina < StaminaMeter.STAMINA_MAX);
  }

  /**
   * Get current stamina
   */
  getStamina(): number {
    return this.stamina;
  }

  /**
   * Deplete stamina by a specified amount
   */
  deplete(amount: number): void {
    this.setStamina(this.stamina - amount);
  }

  /**
   * Show the meter
   */
  show(): void {
    this.container.setVisible(true);
  }

  /**
   * Hide the meter
   */
  hide(): void {
    this.container.setVisible(false);
  }

  /**
   * Destroy the meter
   */
  destroy(): void {
    this.scene.events.off("update", this.update, this);
    this.container.destroy();
  }

  /**
   * Update stamina refill each frame
   * Note: Refill is controlled externally by the controls system
   * This method is now a no-op but kept for backwards compatibility
   */
  private update(): void {
    // Stamina depletion and refilling are handled externally by controls-desktop.ts and controls-touch.ts
    // This ensures proper game state synchronization and prevents conflicts between depletion and refilling
  }
}
